import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { routeNames } from './app-routing-names';
import { QuoteSteps } from './models/enums/quote-steps';
import { AuthenticationGuard } from './guards/authentication-guard';
import { QuoteStepGuard } from './guards/quote-step-guard';
import { InternalAuthenticationGuard } from './guards/internal.authentication-guard';
import { ContentsCalculatorGuard } from './guards/contents-calculator-guard';
import { QuickQuestionsGuard } from './guards/quick-questions-guard';
import { verifyQuickQuestionsGuard } from './guards/verify-quick-questions-guard';

const routes: Routes = [
    {
        path: '',
        redirectTo: routeNames.OWN_RENT_PROPERTY,
        pathMatch: 'full'
    }, {
        path: routeNames.LOGIN,
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
        canActivate: [AuthenticationGuard],
        data: { title: 'Login' }
    }, {
        path: routeNames.OWN_RENT_PROPERTY,
        loadComponent: () => import('./pages/quote-form/about-property/own-rent-property/own-rent-property.component').then(m => m.OwnRentPropertyComponent),
        canActivate: [AuthenticationGuard],
        data: { title: 'Own or Rent Property', step: QuoteSteps.ownPropertyQuestion }
    }, {
        path: routeNames.PROPERTY_TYPE,
        loadComponent: () => import('./pages/quote-form/about-property/property-type/property-type.component').then(m => m.PropertyTypeComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Property type', step: QuoteSteps.propertyType }
    }, {
        path: routeNames.YEAR_BUILD,
        loadComponent: () => import('./pages/quote-form/about-property/year-build/year-build.component').then(m => m.YearBuildComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'year Build', step: QuoteSteps.yearBuild }
    }, {
        path: routeNames.NUMBER_OF_BEDROOMS,
        loadComponent: () => import('./pages/quote-form/about-property/number-of-bedrooms/number-of-bedrooms.component').then(m => m.NumberOfBedroomsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Number of bedrooms', step: QuoteSteps.numberOfBedrooms }
    }, {
        path: routeNames.HEATING_TYPE,
        loadComponent: () => import('./pages/quote-form/about-property/heating-type/heating-type.component').then(m => m.HeatingTypeComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Heating Type', step: QuoteSteps.heatingType }
    }, {
        path: routeNames.ALARM,
        loadComponent: () => import('./pages/quote-form/about-property/alarm/alarm.component').then(m => m.AlarmComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Alarm', step: QuoteSteps.alarm }
    }, {
        path: routeNames.SMOKE_DETECTOR,
        loadComponent: () => import('./pages/quote-form/about-property/smoke-detector/smoke-detector.component').then(m => m.SmokeDetectorComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Smoke Detector', step: QuoteSteps.smokeDetector }
    }, {
        path: routeNames.MY_ADDRESS,
        loadComponent: () => import('./pages/quote-form/about-property/my-address/my-address.component').then(m => m.MyAddressComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'My Address', step: QuoteSteps.myAddress }
    }, {
        path: routeNames.ABOUT_PROPERTY_COMPLETE,
        loadComponent: () => import('./pages/quote-form/about-property/about-property-complete/about-property-complete.component').then(m => m.AboutPropertyCompleteComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'About property complete', step: QuoteSteps.aboutPropertyComplete }
    }, {
        path: routeNames.PREVIOUSLY_HOME_INSURANCE,
        loadComponent: () => import('./pages/quote-form/about-cover/previous-home-insurance/previous-home-insurance.component').then(m => m.PreviousHomeInsuranceComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Previous held insurance', step: QuoteSteps.previouslyHomeInsurance }
    }, {
        path: routeNames.COST_REBUILD_HOME,
        loadComponent: () => import('./pages/quote-form/about-cover/rebuild/rebuild.component').then(m => m.RebuildComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Rebuild', step: QuoteSteps.costRebuildHome }
    }, {
        path: routeNames.CONTENTS_COVER,
        loadComponent: () => import('./pages/quote-form/about-cover/content-cover/content-cover.component').then(m => m.ContentCoverComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Contents cover', step: QuoteSteps.contentsCover }
    }, {
        path: routeNames.POLICY_START_DATE,
        loadComponent: () => import('./pages/quote-form/about-cover/policy-start-date/policy-start-date.component').then(m => m.PolicyStartDateComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Policy start date', step: QuoteSteps.policyStartDate }
    }, {
        path: routeNames.CLAIMS_EXCESS,
        loadComponent: () => import('./pages/quote-form/about-cover/claims-excess/claims-excess.component').then(m => m.ClaimsExcessComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Claims Excess', step: QuoteSteps.claimsExcess }
    }, {
        path: routeNames.ABOUT_COVER_COMPLETE,
        loadComponent: () => import('./pages/quote-form/about-cover/about-cover-complete/about-cover-complete.component').then(m => m.AboutCoverCompleteComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'About Cover Complete', step: QuoteSteps.aboutCoverComplete }
    }, {
        path: routeNames.YOUR_DETAILS,
        loadComponent: () => import('./pages/quote-form/about-you/your-details/your-details.component').then(m => m.YourDetailsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Your details', step: QuoteSteps.yourDetails }
    }, {
        path: routeNames.CONTACT_INFO,
        loadComponent: () => import('./pages/quote-form/about-you/contact-info/contact-info.component').then(m => m.ContactInfoComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Contact info', step: QuoteSteps.contactInfo }
    }, {
        path: routeNames.OTHER_INSURANCE,
        loadComponent: () => import('./pages/quote-form/about-you/other-insurance/other-insurance.component').then(m => m.OtherInsuranceComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Other insurance', step: QuoteSteps.otherInsurance }
    }, {
        path: routeNames.ABOUT_YOU_COMPLETE,
        loadComponent: () => import('./pages/quote-form/about-you/about-you-complete/about-you-complete.component').then(m => m.AboutYouCompleteComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'About you complete', step: QuoteSteps.aboutYouComplete }
    }, {
        path: routeNames.YOUR_QUOTE,
        loadComponent: () => import('./pages/your-quote/your-quote.component').then(m => m.YourQuoteComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Your Quote', step: QuoteSteps.yourQuote }
    }, {
        path: routeNames.OPTIONAL_EXTRAS,
        loadComponent: () => import('./pages/optional-extras/optional-extras.component').then(m => m.OptionalExtrasComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Optional Extras', step: QuoteSteps.optionalExtras }
    }, {
        path: routeNames.PERSONAL_DETAILS,
        loadComponent: () => import('./pages/personal-details/personal-details.component').then(m => m.PersonalDetailsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Personal Details', step: QuoteSteps.personalDetails }
    }, {
        path: routeNames.DECLARATIONS,
        loadComponent: () => import('./pages/declarations/declarations.component').then(m => m.DeclarationsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Declarations', step: QuoteSteps.declarations }
    }, {
        path: routeNames.ACCEPTANCE_QUESTIONS,
        loadComponent: () => import('./pages/acceptance-questions/acceptance-questions.component').then(m => m.AcceptanceQuestionsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Acceptance questions', step: QuoteSteps.acceptanceQuestions }
    }, {
        path: routeNames.ACCEPTANCE_QUESTIONS_DONT_APPLY,
        loadComponent: () => import('./pages/acceptance-questions/acceptance-questions-dont-apply/acceptance-questions-dont-apply.component').then(m => m.AcceptanceQuestionsDontApplyComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Acceptance questions dont apply', step: QuoteSteps.acceptanceQuestionsDontApply }
    }, {
        path: routeNames.CHOOSE_PAYMENT,
        loadComponent: () => import('./pages/choose-payment/choose-payment.component').then(m => m.ChoosePaymentComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Choose payment', step: QuoteSteps.choosePayment }
    }, {
        path: routeNames.PAYMENT,
        loadComponent: () => import('./pages/payment/payment.component').then(m => m.PaymentComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Payment', step: QuoteSteps.payment }
    }, {
        path: routeNames.PAYMENT_DD,
        loadComponent: () => import('./pages/payment-dd/payment-dd.component').then(m => m.PaymentDdComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Payment', step: QuoteSteps.paymentDD }
    },{
        path: routeNames.CONFIRMATION,
        loadComponent: () => import('./pages/confirmation/confirmation.component').then(m => m.ConfirmationComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: { title: 'Confirmation', step: QuoteSteps.confirmation }
    }, {
        path: routeNames.ERROR,
        loadComponent: () => import('./pages/error/error.component').then(m => m.ErrorComponent),
        data: { title: 'Error', step: QuoteSteps.none }
    }, {
        path: routeNames.NO_QUOTE,
        loadComponent: () => import('./pages/no-quote/no-quote.component').then(m => m.NoQuoteComponent),
        data: { title: 'No Quote', step: QuoteSteps.noQuote }
    }, {
        path: routeNames.QUOTE_SEARCH,
        loadComponent: () => import('./pages/quote-search/quote-search.component').then(m => m.QuoteSearchComponent),
        canActivate: [InternalAuthenticationGuard],
        data: { title: 'Quote search' }
    }, {
        path: routeNames.QUOTE_RETRIEVAL,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/quote-retrieval/quote-retrieval.component').then(m => m.QuoteRetrievalComponent),
        data: { title: 'Quote retrieval', step: QuoteSteps.quoteRetrieval }
    }, {
        path: routeNames.QUOTE_SUMMARY,
        canActivate: [AuthenticationGuard, verifyQuickQuestionsGuard],
        loadComponent: () => import('./pages/x-sell/quote-summary/quote-summary.component').then(m => m.QuoteSummaryComponent),
        data: { title: 'Quick Questions Summary', step: QuoteSteps.quickQuestionsSummary }
    }, {
        path: routeNames.VERIFY_STARTING_INFORMATION,
        canActivate: [AuthenticationGuard, QuoteStepGuard, verifyQuickQuestionsGuard],
        loadComponent: () => import('./pages/x-sell/verify-starting-information/verify-starting-information.component').then(m => m.VerifyStartingInformationComponent),
        data: { title: 'Verify Starting Information', step: QuoteSteps.verifyStartingInformation }
    }, {
        path: routeNames.MANAGE_DISCOUNT_LEADS,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/x-sell/manage-discount-leads/manage-discount-leads.component').then(m => m.ManageDiscountLeadsComponent),
        data: { title: 'Manage Discount Leads' }
    }, {
        path: routeNames.OPT_OUT_DISCOUNT_LEADS,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/x-sell/opt-out-discount-leads/opt-out-discount-leads.component').then(m => m.OptOutDiscountLeadsComponent),
        data: { title: 'Opt Out Discount Leads' }
    }, {
        path: routeNames.CONTENTS_CALCULATOR,
        canActivate: [AuthenticationGuard, QuoteStepGuard, ContentsCalculatorGuard],
        canDeactivate: [ContentsCalculatorGuard],
        canActivateChild: [ContentsCalculatorGuard],
        canLoad: [ContentsCalculatorGuard],
        loadChildren: () => import(`./pages/contents-calculator/contents-calculator.module`).then(m => m.ContentsCalculatorModule),
        data: { title: 'Contents Calculator', step: QuoteSteps.contentsCalculator }
    }, {
        path: routeNames.XSELL_QUICK_QUESTIONS,
        canActivate: [AuthenticationGuard],
        canDeactivate: [QuickQuestionsGuard],
        canLoad: [QuickQuestionsGuard],
        loadChildren: () => import(`./pages/xsell-quick-questions/quick-questions.module`).then(m => m.QuickQuestionModule),
        data: { title: 'Quick Questions', step: QuoteSteps.quickQuestions }
    }, {
        path: '**',
        redirectTo: routeNames.OWN_RENT_PROPERTY,
        pathMatch: 'full'
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
    exports: [RouterModule]
})
export class AppRoutingModule { }